import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedHeading, Reveal } from "../components/Reveal";
import { CTAButton } from "../components/CTAButton";
import { Marquee } from "../components/Marquee";
import { SERVICES, MANIFESTO, IMAGES } from "../data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Athlete in stadium" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/85 to-dark/40" />
        <div className="absolute inset-0 bg-dark/30" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-teal mb-6"
        >
          Custom Sports Data Applications
        </motion.p>

        <MaskedHeading
          className="font-display text-5xl sm:text-7xl md:text-8xl font-black uppercase tracking-tighter leading-[0.88]"
          lines={["Every rep.", <>Every record. <span className="text-orange">Searchable.</span></>]}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 max-w-xl text-lg text-slate-300 leading-relaxed"
        >
          We build custom platforms that turn scattered athletic data into structured, intuitive systems
          coaches, directors, and associations actually use.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <CTAButton to="/contact" data-testid="hero-book-demo" variant="orange">Book a Demo</CTAButton>
          <CTAButton to="/products" data-testid="hero-view-products" variant="ghost">Explore Products</CTAButton>
        </motion.div>
      </motion.div>
    </section>
  );
};

const StatsBar = () => (
  <section data-testid="stats-bar" className="border-b border-white/10 bg-dark">
    <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08}>
          <div className="px-6 lg:px-10 py-10 border-r border-white/10 last:border-r-0">
            <p className="font-display text-4xl md:text-5xl font-black text-white">{s.value}</p>
            <p className="font-mono text-xs uppercase tracking-widest text-slate-400 mt-2">{s.label}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const LiveShowcase = () => null;

const ServicesPreview = () => (
  <section data-testid="services-preview" className="py-24 md:py-32 bg-dark-surface border-y border-white/10">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal mb-4">What we do</p>
            <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter max-w-2xl">
              Engineered for athletics
            </h2>
          </div>
          <CTAButton to="/services" variant="ghost" data-testid="services-view-all">All services</CTAButton>
        </div>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {SERVICES.slice(0, 6).map((s, i) => (
          <Reveal key={s.title} delay={(i % 3) * 0.08}>
            <div className="group bg-dark-surface p-8 h-full hover:bg-dark transition-colors duration-300">
              <s.icon className="w-8 h-8 text-teal mb-6 group-hover:text-orange transition-colors duration-300" strokeWidth={1.5} />
              <h3 className="font-display text-2xl font-bold mb-3">{s.title}</h3>
              <p className="text-slate-400 leading-relaxed text-sm">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "20%"]);

  return (
    <section ref={ref} data-testid="manifesto-section" className="relative py-24 md:py-40 overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 z-0 opacity-20">
        <img src={IMAGES.architecture} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-dark/70" />
      </motion.div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-orange mb-16">Our approach</p>
        </Reveal>
        <div className="space-y-px">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.05}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 border-t border-white/15 group">
                <div className="md:col-span-2">
                  <span className="font-display text-6xl md:text-7xl font-black text-transparent" style={{ WebkitTextStroke: "1.5px rgba(0,240,255,0.4)" }}>
                    {m.n}
                  </span>
                </div>
                <h3 className="md:col-span-6 font-display text-3xl md:text-4xl font-extrabold tracking-tight group-hover:text-teal transition-colors duration-300">
                  {m.title}
                </h3>
                <p className="md:col-span-4 text-slate-400 leading-relaxed self-center">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => (
  <section data-testid="cta-section" className="py-24 md:py-32 bg-orange text-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center flex flex-col items-center">
      <Reveal>
        <h2 className="font-display text-4xl sm:text-5xl md:text-7xl font-black uppercase tracking-tighter max-w-4xl">
          Ready to make your data work as hard as your athletes?
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-6 text-lg text-white/80 max-w-xl">
          Book a 30-minute demo and see a system built around your program.
        </p>
      </Reveal>
      <Reveal delay={0.2}>
        <div className="mt-10">
          <CTAButton to="/contact" variant="teal" data-testid="footer-cta-demo">Book a Demo</CTAButton>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div>
      <Hero />
      <Marquee />
      <ServicesPreview />
      <Manifesto />
      <CTASection />
    </div>
  );
}
